import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Pressable,
  StatusBar,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { Spacing, Typography } from '../theme';
import type { ThemeName } from '../theme/themes';
import { useTheme } from '../theme/useTheme';
import { useI18n } from '../i18n';

/**
 * Web counterpart to OnboardingThemeSelection.tsx (native). Same
 * react-native-reanimated barrel-export crash as PhysicalLightSwitch.web.tsx
 * (see that file's comment) — this screen is part of the onboarding flow
 * new users go through, so it can't be skipped or left broken. Same visual
 * language and behavior, built on RN core's Animated API (which already
 * supports color interpolation) instead of reanimated.
 */
const TRANSITION_DURATION_MS = 380;
const EASING = Easing.bezier(0.22, 0.78, 0.24, 1);

interface Props {
  selectedTheme: ThemeName | null;
  saving: boolean;
  error?: string;
  onSelect: (theme: ThemeName) => void;
  onEnter: () => void;
  onBack: () => void;
}

interface ThemePreviewCardProps {
  mode: ThemeName;
  selected: boolean;
  title: string;
  descriptor: string;
  onPress: () => void;
}

const MiniTonytePreview: React.FC<{ mode: ThemeName }> = ({ mode }) => {
  const daylight = mode === 'daylight';
  const palette = daylight
    ? {
        shell: ['#FFFDF8', '#F3E5D1', '#E6D1B5'] as [string, string, string],
        border: 'rgba(132,98,62,0.20)',
        header: '#26221E',
        muted: '#8B7E70',
        image: ['#CDE7E1', '#EFD8BC'] as [string, string],
        chip: 'rgba(255,251,243,0.92)',
        nav: 'rgba(255,252,246,0.94)',
      }
    : {
        shell: ['#172334', '#0D1522', '#070B14'] as [string, string, string],
        border: 'rgba(255,255,255,0.13)',
        header: '#F7F8FA',
        muted: 'rgba(226,233,241,0.52)',
        image: ['#174B59', '#311B4C'] as [string, string],
        chip: 'rgba(255,255,255,0.10)',
        nav: 'rgba(3,8,16,0.88)',
      };

  return (
    <LinearGradient
      colors={palette.shell}
      locations={[0, 0.55, 1]}
      style={[styles.miniShell, { borderColor: palette.border }]}
    >
      <View style={styles.miniHeader}>
        <View style={[styles.miniLogo, { backgroundColor: '#F27D26' }]} />
        <View style={styles.miniHeaderCopy}>
          <View style={[styles.miniLineStrong, { backgroundColor: palette.header }]} />
          <View style={[styles.miniLineMuted, { backgroundColor: palette.muted }]} />
        </View>
        <View style={[styles.miniAvatar, { borderColor: palette.border }]} />
      </View>

      <LinearGradient colors={palette.image} style={styles.miniEventSurface}>
        <View style={styles.miniEventGlow} />
        <View style={styles.miniEventCopy}>
          <View style={[styles.miniEventTitle, { backgroundColor: daylight ? '#34302B' : '#FFFFFF' }]} />
          <View style={[styles.miniEventMeta, { backgroundColor: daylight ? '#746A60' : 'rgba(255,255,255,0.62)' }]} />
        </View>
        <View style={styles.miniCta} />
      </LinearGradient>

      <View style={styles.miniChips}>
        {[0, 1, 2].map(index => (
          <View key={index} style={[styles.miniChip, { backgroundColor: palette.chip, borderColor: palette.border }]} />
        ))}
      </View>

      <View style={[styles.miniNav, { backgroundColor: palette.nav, borderColor: palette.border }]}>
        {[0, 1, 2, 3].map(index => (
          <View
            key={index}
            style={[
              styles.miniNavDot,
              { backgroundColor: index === 0 ? '#F27D26' : palette.muted },
            ]}
          />
        ))}
      </View>
    </LinearGradient>
  );
};

const ThemePreviewCard: React.FC<ThemePreviewCardProps> = ({
  mode,
  selected,
  title,
  descriptor,
  onPress,
}) => {
  const selectedProgress = useRef(new Animated.Value(selected ? 1 : 0)).current;
  const pressProgress = useRef(new Animated.Value(0)).current;
  const daylight = mode === 'daylight';

  useEffect(() => {
    Animated.timing(selectedProgress, {
      toValue: selected ? 1 : 0,
      duration: TRANSITION_DURATION_MS,
      easing: EASING,
      useNativeDriver: false,
    }).start();
  }, [selected, selectedProgress]);

  const handlePressIn = useCallback(() => {
    Animated.timing(pressProgress, {
      toValue: 1,
      duration: 80,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start();
  }, [pressProgress]);

  const handlePressOut = useCallback(() => {
    Animated.timing(pressProgress, {
      toValue: 0,
      duration: 180,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [pressProgress]);

  const animatedCardStyle = {
    opacity: selectedProgress.interpolate({ inputRange: [0, 1], outputRange: [0.88, 1] }),
    borderColor: selectedProgress.interpolate({
      inputRange: [0, 1],
      outputRange: daylight
        ? ['rgba(143,111,76,0.18)', 'rgba(226,126,44,0.82)']
        : ['rgba(255,255,255,0.12)', 'rgba(55,204,205,0.58)'],
    }),
    shadowColor: daylight ? '#C87936' : '#18A8B0',
    shadowOpacity: selectedProgress.interpolate({ inputRange: [0, 1], outputRange: [0.08, daylight ? 0.24 : 0.28] }),
    transform: [
      { translateY: selectedProgress.interpolate({ inputRange: [0, 1], outputRange: [0, -3] }) },
      { scale: pressProgress.interpolate({ inputRange: [0, 1], outputRange: [1, 0.982] }) },
    ],
  };

  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={`${title}. ${descriptor}`}
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Animated.View
        style={[
          styles.themeCard,
          daylight ? styles.daylightCard : styles.midnightCard,
          animatedCardStyle,
        ]}
      >
        <MiniTonytePreview mode={mode} />
        <View style={styles.themeMetaRow}>
          <View style={[styles.themeIcon, daylight ? styles.daylightIcon : styles.midnightIcon]}>
            <Icon name={daylight ? 'sun' : 'moon'} size={18} color={daylight ? '#A94E0D' : '#8BE4E5'} />
          </View>
          <View style={styles.themeCopy}>
            <Text style={[styles.themeTitle, daylight ? styles.daylightTitle : styles.midnightTitle]}>{title}</Text>
            <Text style={[styles.themeDescriptor, daylight ? styles.daylightDescriptor : styles.midnightDescriptor]}>{descriptor}</Text>
          </View>
          <View
            style={[
              styles.selectionCheck,
              daylight ? styles.daylightCheck : styles.midnightCheck,
              !selected && styles.selectionCheckIdle,
            ]}
          >
            {selected ? <Icon name="check" size={13} color={daylight ? '#7A3A0D' : '#D9FFFF'} /> : null}
          </View>
        </View>
      </Animated.View>
    </Pressable>
  );
};

const OnboardingThemeSelection: React.FC<Props> = ({
  selectedTheme,
  saving,
  error,
  onSelect,
  onEnter,
  onBack,
}) => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { themeName } = useTheme();
  const daylightProgress = useRef(new Animated.Value(themeName === 'daylight' ? 1 : 0)).current;
  // The footer floats absolutely over the scroll area (see styles.footer), so
  // its real height is fed back in here to pad the scroll content — otherwise
  // the last card/hint text would render underneath the floating footer.
  const [footerHeight, setFooterHeight] = useState(0);
  const handleFooterLayout = useCallback((event: { nativeEvent: { layout: { height: number } } }) => {
    setFooterHeight(event.nativeEvent.layout.height);
  }, []);

  useEffect(() => {
    Animated.timing(daylightProgress, {
      toValue: themeName === 'daylight' ? 1 : 0,
      duration: TRANSITION_DURATION_MS,
      easing: EASING,
      useNativeDriver: false,
    }).start();
  }, [daylightProgress, themeName]);

  const rootStyle = {
    backgroundColor: daylightProgress.interpolate({ inputRange: [0, 1], outputRange: ['#070B14', '#F5F0E8'] }),
  };
  const daylightLayerStyle = { opacity: daylightProgress };
  const midnightLayerStyle = { opacity: Animated.subtract(1, daylightProgress) };
  const primaryTextStyle = {
    color: daylightProgress.interpolate({ inputRange: [0, 1], outputRange: ['#F7F7F5', '#181817'] }),
  };
  const secondaryTextStyle = {
    color: daylightProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(255,255,255,0.58)', '#756D64'] }),
  };
  const footerStyle = {
    backgroundColor: daylightProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(7,11,20,0.96)', 'rgba(245,240,232,0.97)'] }),
    borderTopColor: daylightProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(255,255,255,0.08)', 'rgba(148,126,102,0.16)'] }),
  };

  return (
    <Animated.View style={[styles.root, rootStyle]}>
      <StatusBar barStyle={themeName === 'daylight' ? 'dark-content' : 'light-content'} translucent backgroundColor="transparent" />

      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, midnightLayerStyle]}>
        <LinearGradient colors={['#07101D', '#0A0E17', '#050812']} style={StyleSheet.absoluteFill} />
        <View style={styles.midnightTealGlow} />
        <View style={styles.midnightPurpleGlow} />
      </Animated.View>
      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, daylightLayerStyle]}>
        <LinearGradient colors={['#EEF5F1', '#F5F0E8', '#EDE3D7']} locations={[0, 0.52, 1]} style={StyleSheet.absoluteFill} />
        <View style={styles.daylightAquaGlow} />
        <View style={styles.daylightWarmGlow} />
      </Animated.View>
      <View pointerEvents="none" style={styles.atmosphericRing} />

      <View
        style={styles.scroll}
      >
        <View style={[styles.content, { paddingTop: insets.top + Spacing.lg, paddingBottom: footerHeight + Spacing.xl }]}>
        <View style={styles.progressRow}>
          {[1, 2, 3, 4, 5, 6, 7].map(dot => (
            <View key={dot} style={[styles.progressDot, dot < 7 && styles.progressDotDone, dot === 7 && styles.progressDotActive]} />
          ))}
        </View>

        <View style={styles.topRow}>
          <Pressable accessibilityRole="button" accessibilityLabel={t('common.back')} onPress={onBack} style={styles.backButton}>
            <Icon name="arrow-left" size={18} color={themeName === 'daylight' ? '#544C44' : 'rgba(255,255,255,0.76)'} />
          </Pressable>
          <Animated.Text style={[styles.kicker, primaryTextStyle]}>{t('onboarding.themeKicker')}</Animated.Text>
          <View style={styles.topRowSpacer} />
        </View>

        <View style={styles.titleBlock}>
          <Animated.Text style={[styles.heading, primaryTextStyle]}>{t('onboarding.themeTitle')}</Animated.Text>
          <Animated.Text style={[styles.subtitle, secondaryTextStyle]}>{t('onboarding.themeSubtitle')}</Animated.Text>
        </View>

        <View style={styles.cards} accessibilityRole="radiogroup">
          <ThemePreviewCard
            mode="daylight"
            selected={selectedTheme === 'daylight'}
            title={t('onboarding.themeDaylight')}
            descriptor={t('onboarding.themeDaylightDescriptor')}
            onPress={() => onSelect('daylight')}
          />
          <ThemePreviewCard
            mode="midnight"
            selected={selectedTheme === 'midnight'}
            title={t('onboarding.themeMidnight')}
            descriptor={t('onboarding.themeMidnightDescriptor')}
            onPress={() => onSelect('midnight')}
          />
        </View>

        <Animated.Text style={[styles.switchHint, secondaryTextStyle]}>{t('onboarding.themeSwitchAnytime')}</Animated.Text>
        </View>
      </View>

      <Animated.View
        onLayout={handleFooterLayout}
        style={[styles.footer, footerStyle, { paddingBottom: insets.bottom + Spacing.md }]}
      >
        <View style={styles.notPermanentRow}>
          <Icon name="user" size={13} color={themeName === 'daylight' ? '#A9703C' : '#F2A157'} />
          <Animated.Text style={[styles.notPermanentText, secondaryTextStyle]}>{t('onboarding.themeNotPermanent')}</Animated.Text>
        </View>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: !selectedTheme || saving }}
          disabled={!selectedTheme || saving}
          onPress={onEnter}
          style={({ pressed }) => [
            styles.enterButton,
            (!selectedTheme || saving) && styles.enterButtonDisabled,
            pressed && selectedTheme && !saving && styles.enterButtonPressed,
          ]}
        >
          <LinearGradient colors={['#FFAA61', '#F27D26', '#C95812']} style={StyleSheet.absoluteFill} />
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <>
              <Text style={styles.enterButtonText}>{t('onboarding.themeEnter')}</Text>
              <Icon name="arrow-right" size={17} color="#FFFFFF" />
            </>
          )}
        </Pressable>
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1 },
  scroll: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehaviorY: 'contain',
  } as any,
  content: { flexGrow: 1, paddingHorizontal: Spacing.lg },
  midnightTealGlow: {
    position: 'absolute', width: 300, height: 300, borderRadius: 150,
    left: -150, top: 80, backgroundColor: 'rgba(38,208,206,0.09)',
  },
  midnightPurpleGlow: {
    position: 'absolute', width: 360, height: 360, borderRadius: 180,
    right: -220, bottom: 80, backgroundColor: 'rgba(111,73,204,0.12)',
  },
  daylightAquaGlow: {
    position: 'absolute', width: 340, height: 340, borderRadius: 170,
    right: -180, top: -60, backgroundColor: 'rgba(38,208,206,0.08)',
  },
  daylightWarmGlow: {
    position: 'absolute', width: 390, height: 390, borderRadius: 195,
    left: -220, bottom: -90, backgroundColor: 'rgba(242,125,38,0.08)',
  },
  atmosphericRing: {
    position: 'absolute', width: 470, height: 470, borderRadius: 235,
    borderWidth: 1, borderColor: 'rgba(242,125,38,0.08)', right: -300, top: 190,
  },
  progressRow: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 7, marginBottom: Spacing.md },
  progressDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: 'rgba(130,120,110,0.24)' },
  progressDotDone: { backgroundColor: 'rgba(38,208,206,0.62)' },
  progressDotActive: { width: 28, backgroundColor: '#F27D26' },
  topRow: { minHeight: 44, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  backButton: {
    width: 42, height: 42, borderRadius: 16, alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: 'rgba(143,121,96,0.18)', backgroundColor: 'rgba(255,255,255,0.10)',
  },
  topRowSpacer: { width: 42 },
  kicker: { fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 2.1, textAlign: 'center' },
  titleBlock: { alignItems: 'center', gap: 7, marginTop: Spacing.sm, marginBottom: Spacing.lg },
  heading: { fontSize: 32, lineHeight: 38, fontWeight: Typography.extrabold, textAlign: 'center' },
  subtitle: { maxWidth: 320, fontSize: Typography.sm, lineHeight: 20, textAlign: 'center' },
  cards: { gap: Spacing.base },
  themeCard: {
    minHeight: 218, borderRadius: 26, borderWidth: 1.5, padding: Spacing.md,
    shadowOffset: { width: 0, height: 10 }, shadowRadius: 22, elevation: 8,
  },
  daylightCard: { backgroundColor: '#F8EFE2' },
  midnightCard: { backgroundColor: '#0B111D' },
  miniShell: { height: 134, borderRadius: 19, borderWidth: 1, padding: 10, overflow: 'hidden' },
  miniHeader: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  miniLogo: { width: 16, height: 16, borderRadius: 6 },
  miniHeaderCopy: { flex: 1, gap: 4 },
  miniLineStrong: { width: '37%', height: 4, borderRadius: 2 },
  miniLineMuted: { width: '24%', height: 3, borderRadius: 2, opacity: 0.58 },
  miniAvatar: { width: 17, height: 17, borderRadius: 9, borderWidth: 1, backgroundColor: 'rgba(242,125,38,0.16)' },
  miniEventSurface: { height: 58, borderRadius: 13, marginTop: 9, padding: 8, justifyContent: 'flex-end', overflow: 'hidden' },
  miniEventGlow: { position: 'absolute', width: 80, height: 80, borderRadius: 40, right: -15, top: -30, backgroundColor: 'rgba(242,125,38,0.18)' },
  miniEventCopy: { gap: 4 },
  miniEventTitle: { width: '48%', height: 5, borderRadius: 3 },
  miniEventMeta: { width: '34%', height: 3, borderRadius: 2 },
  miniCta: { position: 'absolute', right: 8, bottom: 8, width: 32, height: 14, borderRadius: 7, backgroundColor: '#F27D26' },
  miniChips: { flexDirection: 'row', gap: 5, marginTop: 7 },
  miniChip: { width: 36, height: 10, borderRadius: 5, borderWidth: 1 },
  miniNav: { position: 'absolute', right: 10, bottom: 9, width: 70, height: 17, borderRadius: 9, borderWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' },
  miniNavDot: { width: 4, height: 4, borderRadius: 2 },
  themeMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 11, marginTop: 12 },
  themeIcon: { width: 38, height: 38, borderRadius: 14, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  daylightIcon: { backgroundColor: 'rgba(242,125,38,0.12)', borderColor: 'rgba(189,100,31,0.20)' },
  midnightIcon: { backgroundColor: 'rgba(38,208,206,0.09)', borderColor: 'rgba(38,208,206,0.20)' },
  themeCopy: { flex: 1 },
  themeTitle: { fontSize: Typography.base, fontWeight: Typography.extrabold, letterSpacing: 0.8 },
  themeDescriptor: { marginTop: 2, fontSize: Typography.xs, fontWeight: Typography.medium },
  daylightTitle: { color: '#211E1B' },
  daylightDescriptor: { color: '#786D62' },
  midnightTitle: { color: '#F7F8FA' },
  midnightDescriptor: { color: 'rgba(231,237,243,0.58)' },
  selectionCheck: { width: 27, height: 27, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  daylightCheck: { backgroundColor: '#F3C899', borderColor: '#D77A2D' },
  midnightCheck: { backgroundColor: 'rgba(38,208,206,0.18)', borderColor: 'rgba(71,224,225,0.62)' },
  selectionCheckIdle: { backgroundColor: 'transparent', borderColor: 'rgba(140,130,120,0.25)' },
  switchHint: { textAlign: 'center', fontSize: Typography.xs, lineHeight: 18, marginTop: Spacing.lg },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderTopWidth: 1,
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.md,
  },
  notPermanentRow: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 6, marginBottom: 10 },
  notPermanentText: { fontSize: 11, textAlign: 'center' },
  errorText: { color: '#F27D26', fontSize: Typography.xs, textAlign: 'center', marginBottom: 8 },
  enterButton: {
    minHeight: 56, borderRadius: 19, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 9,
    overflow: 'hidden', shadowColor: '#F27D26', shadowOffset: { width: 0, height: 7 }, shadowOpacity: 0.30, shadowRadius: 16, elevation: 9,
  },
  enterButtonDisabled: { opacity: 0.38 },
  enterButtonPressed: { transform: [{ scale: 0.985 }] },
  enterButtonText: { color: '#FFFFFF', fontSize: Typography.base, fontWeight: Typography.extrabold },
});

export default OnboardingThemeSelection;
